import type { Risk } from '@rtkwlf/mr-infra-risk';
import { useTranslation } from 'react-i18next';

import { DateTime } from '@packages/components/date-time/date-time';
import { DateTimeWithNewBadge } from '@packages/components/date-time-with-new-badge';
import { Box, Grid, Typography } from '@packages/fenrir/components';

import { ListWithDrawer } from '../../../components/list-with-drawer/list-with-drawer';
import { RowExpansionItem } from '../../../components/row-expansion-item/row-expansion-item';
import { useUserMapAndOptions } from '../use-user-map-and-options/use-user-map-and-options';

type RiskRowExpansionProps = {
  row: Risk;
};

export const RiskRowExpansion: React.FC<RiskRowExpansionProps> = ({ row }) => {
  const { t } = useTranslation('riskManagement', {
    keyPrefix: 'risks',
  });
  const { data } = useUserMapAndOptions();
  const { userMap } = data || {};
  const assignee =
    userMap && row?.management.assignee
      ? userMap[row?.management.assignee]
      : t('unassigned');

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={2}>
        <Grid size={9}>
          <RowExpansionItem title={t('description')}>
            <Typography variant="body2">{row.description}</Typography>
          </RowExpansionItem>
        </Grid>
        <Grid size={3} sx={{ paddingBottom: 1 }}>
          <RowExpansionItem title={t('assignedTo')}>
            <Typography variant="body2">{assignee}</Typography>
          </RowExpansionItem>
          <RowExpansionItem title={t('dueDate')}>
            {row.management?.deadline && (
              <DateTime value={row.management.deadline} />
            )}
          </RowExpansionItem>
        </Grid>
        <Grid size={9}>
          <RowExpansionItem title={t('remediation')}>
            {row.recommendations?.length && (
              <ListWithDrawer
                title={t('remediation')}
                list={row.recommendations}
              />
            )}
          </RowExpansionItem>
        </Grid>
        <Grid size={3}>
          <RowExpansionItem title={t('tableHeaders.firstDetectedTime')}>
            {row.firstDetectedTime && (
              <DateTimeWithNewBadge dateTime={row.firstDetectedTime} />
            )}
          </RowExpansionItem>
          <RowExpansionItem title={t('tableHeaders.lastDetectedTime')}>
            {row.lastDetectedTime && <DateTime value={row.lastDetectedTime} />}
          </RowExpansionItem>
        </Grid>
      </Grid>
    </Box>
  );
};
